import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
  FetchClientListLienholdersInput,
  GraphQLClient,
  fetchClientListLienholders,
  fetchRhlClientList,
  fetchLenderDetail,
  fetchLenderLienholderDetail,
  fetchLienholderDetail,
  createManyTargetRecoveryRatesLender,
  fetchTargetRecoveryRatesByUser,
  fetchTargetRecoveryRatesLienholderByUser,
  updateManyTargetRecoveryRatesLender,
  createManyTargetRecoveryRatesLienholder,
  updateManyTargetRecoveryRatesLienholder,
  UpdateTargetRecoveryRateLenderInput,
  UpdateTargetRecoveryRateLienholderInput,
  fetchNewTopRecoveryAgents,
  fetchNewTopRecoveryAgentsViewAll,
  fetchRepossessionsCount,
  fetchAssignmentsOverview,
  FetchLenderDetailInput as FetchLenderDetailInputType,
  FetchLienholderDetailInput as FetchLienholderDetailInputType,
} from '@rapid-recovery-agency-inc/insightt-shared';
import { TargetRecoveryRateDuration, TopRecoveryAgentQuery } from '@rapid-recovery-agency-inc/insightt-graphql';
import type { TopRecoveryAgentViewAllOutput } from '@rapid-recovery-agency-inc/insightt-graphql';
import moment from 'moment-timezone';

import { client } from '../../shared/apollo';
import { InsighttNotification } from '../../helpers/insightt-notification';
import { tryAndCatch } from '../../shared/error';
import { reportsService } from '../../services/insightt-services';
import { AppState } from '../../redux/store';
import {
  FetchDailyViewStatsParams,
  FetchMtdAssignedParams,
  FetchMtdRepossessedParams,
  FetchNewTopAgentsParams,
  FetchPcymCompanyStatsParams,
  FetchRepoHitListClientsParams,
  FetchTopAgentsParams,
  FetchYtdCompanyStatsParams,
} from './reports-types';
import {
  FETCH_RHL_CLIENTS_THUNK_TYPE,
  FETCH_RHL_CLIENTS_ERROR_MSG,
  FETCH_PCYM_COMPANY_STATS_THUNK_TYPE,
  FETCH_YTD_COMPANY_STATS_THUNK_TYPE,
  FETCH_TOP_AGENTS_THUNK_TYPE,
  FETCH_DAILY_VIEW_STATS_THUNK_TYPE,
  FETCH_MTD_REPOSSESSED_THUNK_TYPE,
  FETCH_MTD_ASSIGNED_THUNK_TYPE,
  FETCH_CLIENT_LIST_WITH_LIENHOLDERS,
  FETCH_CLIENT_LIST_WITH_LIENHOLDERS_ERROR_MSG,
  FETCH_LENDER_DETAIL,
  FETCH_LENDER_DETAIL_ERROR_MSG,
  FETCH_LIENHOLDER_DETAIL,
  FETCH_LIENHOLDER_DETAIL_ERROR_MSG,
  FETCH_LIENHOLDER_LENDER_DETAIL,
  FETCH_LIENHOLDER_LENDER_DETAIL_ERROR_MSG,
  CREATE_TRR_LENDER,
  CREATE_TRR_LENDER_ERROR_MSG,
  UPSERT_TRR_LENDER,
  UPSERT_TRR_LENDER_ERROR_MSG,
  UPSERT_TRR_LIENHOLDER,
  UPSERT_TRR_LIENHOLDER_ERROR_MSG,
  UPDATE_TRR_LENDER,
  UPDATE_TRR_LENDER_ERROR_MSG,
  CREATE_TRR_LIENHOLDER,
  CREATE_TRR_LIENHOLDER_ERROR_MSG,
  UPDATE_TRR_LIENHOLDER,
  UPDATE_TRR_LIENHOLDER_ERROR_MSG,
  FETCH_TOP_AGENTS_VIEW_ALL_THUNK_TYPE,
  FETCH_TOP_AGENTS_THUNK_ERROR,
  FETCH_TOP_AGENTS_VIEW_ALL_THUNK_ERROR,
  // FETCH_MTDYTD_COMPANY_UNCATEGORIZED_THUNK_TYPE,
} from './reports-constants';
import { formattedInput, separateLienholderTRRItems, separateTRRItems } from './utils';

const apolloClient = client as unknown as GraphQLClient;
interface FetchLenderDetailInput extends Omit<FetchLenderDetailInputType, 'timezone'> {
  timezone?: string;
}
interface FetchLienholderDetailInput extends Omit<FetchLienholderDetailInputType, 'timezone'> {
  clientId?: string | null;
  timezone?: string;
}
// --- Interfaces for stronger type safety ---
type CreateTRRLienholderInput = Parameters<typeof createManyTargetRecoveryRatesLienholder>[1];

// --- Thunks and Actions ---

export const setSpottersFirstLoading = createAction<boolean>('REPORTS/SET_SPOTTERS_FIRST_LOADING');

export const fetchRHLClientList = createAsyncThunk(
  FETCH_RHL_CLIENTS_THUNK_TYPE,
  async (data: FetchRepoHitListClientsParams, thunkApi) => {
    try {
      const response = await fetchRhlClientList(apolloClient, data);

      if (!response) throw new Error(FETCH_RHL_CLIENTS_ERROR_MSG);

      return thunkApi.fulfillWithValue({ rhlClientList: response });
    } catch {
      InsighttNotification('error', FETCH_RHL_CLIENTS_ERROR_MSG);
      return thunkApi.rejectWithValue(FETCH_RHL_CLIENTS_ERROR_MSG);
    }
  },
);

export const fetchPcymCompanyStatsAction = createAsyncThunk(
  FETCH_PCYM_COMPANY_STATS_THUNK_TYPE,
  async (data: FetchPcymCompanyStatsParams, thunkApi) => {
    const { year, timezoneOffset } = data;
    const [response, error] = await tryAndCatch(reportsService.getPCYMCompanyStats(year, timezoneOffset));

    if (error) {
      InsighttNotification('error', error.message);
      return thunkApi.rejectWithValue(error.message);
    }

    return thunkApi.fulfillWithValue({ pcymCompanyStats: response.data });
  },
);

export const fetchYtdCompanyStatsAction = createAsyncThunk(
  FETCH_YTD_COMPANY_STATS_THUNK_TYPE,
  async (data: FetchYtdCompanyStatsParams, thunkApi) => {
    const { date, timezoneOffset } = data;
    const [response, error] = await tryAndCatch(reportsService.getYTDCompanyStats(date, timezoneOffset));

    if (error) {
      InsighttNotification('error', error.message);
      return thunkApi.rejectWithValue(error.message);
    }

    return thunkApi.fulfillWithValue({ ytdCompanyStats: response.data });
  },
);

export const fetchTopAgentsAction = createAsyncThunk(
  FETCH_TOP_AGENTS_THUNK_TYPE,
  async (data: FetchTopAgentsParams, thunkApi) => {
    const { startDate, endDate, timezoneOffset } = data;
    const [response, error] = await tryAndCatch(reportsService.getTopAgents(startDate, endDate, timezoneOffset));

    if (error) {
      InsighttNotification('error', error.message);
      return thunkApi.rejectWithValue(error.message);
    }

    return thunkApi.fulfillWithValue({ topAgents: response.data });
  },
);

export const fetchDailyViewStatsAction = createAsyncThunk(
  FETCH_DAILY_VIEW_STATS_THUNK_TYPE,
  async (data: FetchDailyViewStatsParams, thunkApi) => {
    const { date, timezoneOffset, branchId } = data;
    const [response, error] = await tryAndCatch(reportsService.getDailyViewStats(date, timezoneOffset, branchId));

    if (error) {
      InsighttNotification('error', error.message);
      return thunkApi.rejectWithValue(error.message);
    }

    return thunkApi.fulfillWithValue({ dailyViewStats: response.data.data });
  },
);

export const fetchMtdRepossessedAction = createAsyncThunk(
  FETCH_MTD_REPOSSESSED_THUNK_TYPE,
  async (data: FetchMtdRepossessedParams, thunkApi) => {
    const { startDate, endDate, branchId, mode, fetchPolicy } = data;

    const store = thunkApi.getState() as AppState;
    const timezone = store.system.session.timezone;

    if (!timezone) {
      return thunkApi.rejectWithValue(new Error('No timezone'));
    }

    const startDateTime = moment.tz(startDate, timezone).startOf('day').toISOString();
    const endDateTime = moment.tz(endDate, timezone).endOf('day').toISOString();

    const input = {
      startDateTime,
      endDateTime,
      branchId,
      fetchPolicy,
    };
    const [response, error] = await tryAndCatch(fetchRepossessionsCount(client, input));

    if (error) {
      InsighttNotification('error', error.message);
      return thunkApi.rejectWithValue(error.message);
    }

    return thunkApi.fulfillWithValue({
      mtdRepossessed: response.count,
      mode,
    });
  },
);

export const fetchMtdAssignedAction = createAsyncThunk(
  FETCH_MTD_ASSIGNED_THUNK_TYPE,
  async (data: FetchMtdAssignedParams, thunkApi) => {
    const { startDate, endDate, branchId, fetchPolicy } = data;

    const input = {
      startDate,
      endDate,
      branchId: branchId === -1 ? null : branchId,
      fetchPolicy,
    };
    const [response, error] = await tryAndCatch(fetchAssignmentsOverview(client, input));

    if (error) {
      InsighttNotification('error', error.message);
      return thunkApi.rejectWithValue(error.message);
    }

    const { current, previous } = response;

    return thunkApi.fulfillWithValue({
      current,
      previous,
    });
  },
);

export const fetchClientListWithLienholders = createAsyncThunk(
  FETCH_CLIENT_LIST_WITH_LIENHOLDERS,
  async (input: FetchClientListLienholdersInput, thunkApi) => {
    try {
      const response = await fetchClientListLienholders(apolloClient, input);

      if (!response) throw new Error(FETCH_CLIENT_LIST_WITH_LIENHOLDERS_ERROR_MSG);

      return thunkApi.fulfillWithValue(response);
    } catch {
      InsighttNotification('error', FETCH_CLIENT_LIST_WITH_LIENHOLDERS_ERROR_MSG);
      return thunkApi.rejectWithValue(FETCH_CLIENT_LIST_WITH_LIENHOLDERS_ERROR_MSG);
    }
  },
);

// Client Details
export const openEditClientGoalModal = createAction<void>('OPEN_EDIT_CLIENT_GOAL_MODAL_LENDER');
export const closeEditClientGoalModal = createAction<void>('CLOSE_EDIT_CLIENT_GOAL_MODAL_LENDER');
export const openEditClientGoalModalLienholder = createAction<void>('OPEN_EDIT_CLIENT_GOAL_MODAL_LIENHOLDER');
export const closeEditClientGoalModalLienholder = createAction<void>('CLOSE_EDIT_CLIENT_GOAL_MODAL_LIENHOLDER');

export const fetchLenderDetails = createAsyncThunk(
  FETCH_LENDER_DETAIL,
  async (input: FetchLenderDetailInput, thunkApi) => {
    try {
      let response = await fetchLenderDetail(apolloClient, input);

      if (!response) throw new Error(FETCH_LENDER_DETAIL_ERROR_MSG);
      response = response[0];

      return thunkApi.fulfillWithValue(response);
    } catch {
      InsighttNotification('error', FETCH_LENDER_DETAIL_ERROR_MSG);
      return thunkApi.rejectWithValue(FETCH_LENDER_DETAIL_ERROR_MSG);
    }
  },
);

export const fetchLienholderDetails = createAsyncThunk(
  FETCH_LIENHOLDER_DETAIL,
  async (input: FetchLienholderDetailInput, thunkApi) => {
    try {
      let response = await fetchLienholderDetail(apolloClient, input);

      if (!response) throw new Error(FETCH_LIENHOLDER_DETAIL_ERROR_MSG);
      response = response[0];

      return thunkApi.fulfillWithValue(response);
    } catch {
      InsighttNotification('error', FETCH_LIENHOLDER_DETAIL_ERROR_MSG);
      return thunkApi.rejectWithValue(FETCH_LIENHOLDER_DETAIL_ERROR_MSG);
    }
  },
);

export const fetchLienholderWithLenderDetails = createAsyncThunk(
  FETCH_LIENHOLDER_LENDER_DETAIL,
  async (input: FetchLienholderDetailInput, thunkApi) => {
    try {
      let response = await fetchLenderLienholderDetail(apolloClient, input);

      if (!response) throw new Error(FETCH_LIENHOLDER_LENDER_DETAIL_ERROR_MSG);
      response = response[0];

      return thunkApi.fulfillWithValue(response);
    } catch {
      InsighttNotification('error', FETCH_LIENHOLDER_LENDER_DETAIL_ERROR_MSG);
      return thunkApi.rejectWithValue(FETCH_LIENHOLDER_LENDER_DETAIL_ERROR_MSG);
    }
  },
);

// Create TRR Lender
export const createTRRLender = createAsyncThunk(
  CREATE_TRR_LENDER,
  async (input: Parameters<typeof createManyTargetRecoveryRatesLender>[1], thunkApi) => {
    try {
      const response = await createManyTargetRecoveryRatesLender(apolloClient, input);
      if (!response) throw new Error(CREATE_TRR_LENDER_ERROR_MSG);

      return thunkApi.fulfillWithValue(response);
    } catch {
      InsighttNotification('error', CREATE_TRR_LENDER_ERROR_MSG);
      return thunkApi.rejectWithValue(CREATE_TRR_LENDER_ERROR_MSG);
    }
  },
);
// Update TRR Lender
export const updateTRRLender = createAsyncThunk(
  UPDATE_TRR_LENDER,
  async (input: UpdateTargetRecoveryRateLenderInput, thunkApi) => {
    try {
      const response = await updateManyTargetRecoveryRatesLender(apolloClient, input);
      if (!response) throw new Error(UPDATE_TRR_LENDER_ERROR_MSG);

      return thunkApi.fulfillWithValue(response);
    } catch {
      InsighttNotification('error', UPDATE_TRR_LENDER_ERROR_MSG);
      return thunkApi.rejectWithValue(UPDATE_TRR_LENDER_ERROR_MSG);
    }
  },
);

export interface UpsertTRRLenderInput {
  branchIdUrl: number;
  branchIds: string[];
  clientId: string;
  dateRanges: TargetRecoveryRateDuration[] | string[];
  datePeriodUrl: string;
  orderTypes?: (string | null)[];
  orderTypeUrl?: string;
  targetRecoveryRate: number;
  userId: number;
  endDateTime: string;
  startDateTime: string;
}

export const upsertTRRLender = createAsyncThunk(
  UPSERT_TRR_LENDER,
  async (input: UpsertTRRLenderInput, thunkApi) => {
    const {
      branchIds,
      clientId,
      dateRanges,
      targetRecoveryRate,
      userId,
      endDateTime,
      startDateTime,
      branchIdUrl,
      datePeriodUrl,
    } = input;

    // Format data for Create
    const formatted: Parameters<typeof separateTRRItems>[0] = [];
    dateRanges.forEach((dateRange) => {
      branchIds.forEach((branchId) => {
        formatted.push({
          clientId,
          userId,
          branchId: branchId === '0' ? null : Number(branchId),
          duration: dateRange,
          targetRecoveryRate,
        });
      });
    });

    try {
      // Get TRR for Lenders
      const lenderTRRForUser = await fetchTargetRecoveryRatesByUser(apolloClient, userId);

      const { trrInDB, trrInDBNull, trrNotInDB } = separateTRRItems(formatted, lenderTRRForUser);

      // Create
      if (trrNotInDB.length > 0) {
        await thunkApi.dispatch(createTRRLender(trrNotInDB as never));
      }
      // Format data for Update
      const updateInput: UpdateTargetRecoveryRateLenderInput = {
        clientId: String(clientId) as unknown as number,
        userId,
        targetRecoveryRate: String(targetRecoveryRate),
        updateBranches: trrInDB.map((trrData) => trrData.branchId) as number[],
        updateDurations: dateRanges,
      };
      const updateInputNull: UpdateTargetRecoveryRateLenderInput = {
        clientId: String(clientId) as unknown as number,
        userId,
        targetRecoveryRate: String(targetRecoveryRate),
        updateBranches: trrInDBNull.map((trrData) => trrData.branchId) as null[],
        updateDurations: dateRanges as TargetRecoveryRateDuration[],
      };

      // Update
      if (updateInput.updateBranches.length > 0 && Number(updateInput.targetRecoveryRate) > 0) {
        await thunkApi.dispatch(updateTRRLender(updateInput));
      }
      if (updateInputNull.updateBranches.length > 0 && Number(updateInputNull.targetRecoveryRate) > 0) {
        await thunkApi.dispatch(updateTRRLender(updateInputNull));
      }

      InsighttNotification('success', 'Successfully updated your target recovery rate!');

      thunkApi.dispatch(
        fetchLenderDetails({
          clientId: clientId as string,
          branchId: branchIdUrl,
          duration: datePeriodUrl as 'MTD',
          endDateTime,
          startDateTime,
          userId,
        }),
      );
    } catch {
      InsighttNotification('error', UPSERT_TRR_LENDER_ERROR_MSG);
      return thunkApi.rejectWithValue(UPSERT_TRR_LENDER_ERROR_MSG);
    }
  },
);

export const upsertTRRLenderV2 = createAsyncThunk(
  UPSERT_TRR_LENDER,
  async (input: UpsertTRRLenderInput, thunkApi) => {
    const {
      branchIds,
      clientId,
      dateRanges,
      orderTypes = [],
      targetRecoveryRate,
      userId,
      endDateTime,
      startDateTime,
      branchIdUrl,
      orderTypeUrl,
      datePeriodUrl,
    } = input;

    // Format data for Create
    const formatted = formattedInput(branchIds, dateRanges, orderTypes, targetRecoveryRate, userId, clientId);

    try {
      const promises = [];

      // Get TRR for Lenders
      const lenderTRRForUser = await fetchTargetRecoveryRatesByUser(apolloClient, userId);
      const { trrInDB, trrInDBNull, trrNotInDB } = separateTRRItems(formatted, lenderTRRForUser);

      // Create
      if (trrNotInDB.length > 0) promises.push(thunkApi.dispatch(createTRRLender(trrNotInDB as never)));

      // Format data for Update
      const updateInput: UpdateTargetRecoveryRateLenderInput = {
        clientId: String(clientId) as unknown as number,
        userId,
        targetRecoveryRate: String(targetRecoveryRate),
        updateBranches: trrInDB.map((trrData) => trrData.branchId) as number[],
        updateDurations: dateRanges as TargetRecoveryRateDuration[],
      };

      const updateInputNull: UpdateTargetRecoveryRateLenderInput = {
        clientId: String(clientId) as unknown as number,
        userId,
        targetRecoveryRate: String(targetRecoveryRate),
        updateBranches: trrInDBNull.map((trrData) => trrData.branchId) as null[],
        updateDurations: dateRanges as TargetRecoveryRateDuration[],
      };

      // Update
      if (updateInput.updateBranches.length > 0 && Number(updateInput.targetRecoveryRate) > 0)
        promises.push(thunkApi.dispatch(updateTRRLender(updateInput)));
      if (updateInputNull.updateBranches.length > 0 && Number(updateInputNull.targetRecoveryRate) > 0)
        promises.push(thunkApi.dispatch(updateTRRLender(updateInputNull)));

      await Promise.all(promises);

      return thunkApi.dispatch(
        fetchLenderDetails({
          clientId: clientId as string,
          branchId: branchIdUrl,
          duration: datePeriodUrl as 'MTD',
          orderType: orderTypeUrl,
          endDateTime,
          startDateTime,
          userId,
        }),
      );
    } catch {
      InsighttNotification('error', UPSERT_TRR_LENDER_ERROR_MSG);
      return thunkApi.rejectWithValue(UPSERT_TRR_LENDER_ERROR_MSG);
    }
  },
);

// Create TRR Lienholder
export const createTRRLienholder = createAsyncThunk(
  CREATE_TRR_LIENHOLDER,
  async (input: CreateTRRLienholderInput, thunkApi) => {
    try {
      const response = await createManyTargetRecoveryRatesLienholder(apolloClient, input);
      if (!response) throw new Error(CREATE_TRR_LIENHOLDER_ERROR_MSG);

      return thunkApi.fulfillWithValue(response);
    } catch {
      InsighttNotification('error', CREATE_TRR_LIENHOLDER_ERROR_MSG);
      return thunkApi.rejectWithValue(CREATE_TRR_LIENHOLDER_ERROR_MSG);
    }
  },
);

// Update TRR Lienholder
export const updateTRRLienholder = createAsyncThunk(
  UPDATE_TRR_LIENHOLDER,
  async (input: UpdateTargetRecoveryRateLienholderInput, thunkApi) => {
    try {
      const response = await updateManyTargetRecoveryRatesLienholder(apolloClient, input);
      if (!response) throw new Error(UPDATE_TRR_LIENHOLDER_ERROR_MSG);

      return thunkApi.fulfillWithValue(response);
    } catch {
      InsighttNotification('error', UPDATE_TRR_LIENHOLDER_ERROR_MSG);
      return thunkApi.rejectWithValue(UPDATE_TRR_LIENHOLDER_ERROR_MSG);
    }
  },
);

export interface UpsertTRRLienholderInput {
  branchIdUrl: number;
  branchIds: string[];
  clientId?: string | null;
  lienholderId: string;
  dateRanges: string[];
  datePeriodUrl: string;
  orderTypes?: (string | null)[];
  orderTypeUrl?: string;
  targetRecoveryRate: number;
  userId: number;
  endDateTime: string;
  startDateTime: string;
}

export const upsertTRRLienholder = createAsyncThunk(
  UPSERT_TRR_LIENHOLDER,
  async (input: UpsertTRRLienholderInput, thunkApi) => {
    const {
      branchIds,
      clientId,
      lienholderId,
      dateRanges,
      targetRecoveryRate,
      userId,
      branchIdUrl,
      datePeriodUrl,
      endDateTime,
      startDateTime,
    } = input;

    // Format data
    const formatted: Parameters<typeof separateLienholderTRRItems>[0] = [];
    dateRanges.forEach((dateRange) => {
      branchIds.forEach((branchId) => {
        const inputObj: CreateTRRLienholderInput[0] = {
          userId,
          branchId: branchId ? Number(branchId) : null,
          duration: dateRange as never,
          targetRecoveryRate: Number(targetRecoveryRate),
        };
        if (clientId) inputObj['clientId'] = clientId;
        if (lienholderId) inputObj['lienholderId'] = lienholderId;

        formatted.push(inputObj as never);
      });
    });

    try {
      // Get TRR for Lienholders
      const lienholderTRRForUser = await fetchTargetRecoveryRatesLienholderByUser(apolloClient, userId);

      const { trrInDB, trrInDBNull, trrNotInDB } = separateLienholderTRRItems(formatted, lienholderTRRForUser);

      // Create
      if (trrNotInDB.length > 0) {
        await thunkApi.dispatch(createTRRLienholder(trrNotInDB as never));
      }

      // Format data for Update
      const updateInput: UpdateTargetRecoveryRateLienholderInput = {
        lienholderId,
        userId,
        targetRecoveryRate: String(targetRecoveryRate),
        updateBranches: trrInDB.map((trrData) => trrData.branchId) as number[],
        updateDurations: dateRanges as TargetRecoveryRateDuration[],
      };
      const updateInputNull: UpdateTargetRecoveryRateLienholderInput = {
        lienholderId,
        userId,
        targetRecoveryRate: String(targetRecoveryRate),
        updateBranches: trrInDBNull.map((trrData) => trrData.branchId) as null[],
        updateDurations: dateRanges as TargetRecoveryRateDuration[],
      };

      // Update
      if (updateInput.updateBranches.length > 0 && Number(updateInput.targetRecoveryRate) > 0) {
        await thunkApi.dispatch(updateTRRLienholder(updateInput));
      }
      if (updateInputNull.updateBranches.length > 0 && Number(updateInputNull.targetRecoveryRate) > 0) {
        await thunkApi.dispatch(updateTRRLienholder(updateInputNull));
      }

      InsighttNotification('success', 'Successfully updated your target recovery rate!');

      const fetchInput: FetchLienholderDetailInput = {
        lienholderId,
        branchId: branchIdUrl,
        duration: datePeriodUrl as 'MTD',
        endDateTime,
        startDateTime,
        userId,
      };
      if (!clientId) {
        thunkApi.dispatch(fetchLienholderDetails(fetchInput));
        return;
      }

      if (clientId) fetchInput['clientId'] = clientId;
      thunkApi.dispatch(fetchLienholderWithLenderDetails(fetchInput));
    } catch {
      InsighttNotification('error', UPSERT_TRR_LIENHOLDER_ERROR_MSG);
      return thunkApi.rejectWithValue(UPSERT_TRR_LIENHOLDER_ERROR_MSG);
    }
  },
);

export const upsertTRRLienholderV2 = createAsyncThunk(
  UPSERT_TRR_LIENHOLDER,
  async (input: UpsertTRRLienholderInput, thunkApi) => {
    const {
      branchIds,
      clientId,
      lienholderId,
      dateRanges,
      targetRecoveryRate,
      userId,
      orderTypes = [],
      orderTypeUrl,
      branchIdUrl,
      datePeriodUrl,
      endDateTime,
      startDateTime,
    } = input;

    // Format data
    const formatted = formattedInput(
      branchIds,
      dateRanges,
      orderTypes,
      targetRecoveryRate,
      userId,
      clientId,
      lienholderId,
    );

    try {
      const promises = [];

      // Get TRR for Lienholders
      const lienholderTRRForUser = await fetchTargetRecoveryRatesLienholderByUser(apolloClient, userId);
      const { trrInDB, trrInDBNull, trrNotInDB } = separateLienholderTRRItems(formatted, lienholderTRRForUser);

      // Create
      if (trrNotInDB.length > 0) promises.push(thunkApi.dispatch(createTRRLienholder(trrNotInDB as never)));

      // Format data for Update
      const updateInput: UpdateTargetRecoveryRateLienholderInput = {
        lienholderId,
        userId,
        targetRecoveryRate: String(targetRecoveryRate),
        updateBranches: trrInDB.map((trrData) => trrData.branchId) as number[],
        updateDurations: dateRanges as TargetRecoveryRateDuration[],
      };

      const updateInputNull: UpdateTargetRecoveryRateLienholderInput = {
        lienholderId,
        userId,
        targetRecoveryRate: String(targetRecoveryRate),
        updateBranches: trrInDBNull.map((trrData) => trrData.branchId) as null[],
        updateDurations: dateRanges as TargetRecoveryRateDuration[],
      };

      // Update
      if (updateInput.updateBranches.length > 0 && Number(updateInput.targetRecoveryRate) > 0)
        promises.push(thunkApi.dispatch(updateTRRLienholder(updateInput)));
      if (updateInputNull.updateBranches.length > 0 && Number(updateInputNull.targetRecoveryRate) > 0)
        promises.push(thunkApi.dispatch(updateTRRLienholder(updateInputNull)));

      const fetchInput: FetchLienholderDetailInput = {
        lienholderId,
        branchId: branchIdUrl,
        duration: datePeriodUrl as 'MTD',
        orderType: orderTypeUrl,
        endDateTime,
        startDateTime,
        userId,
      };
      if (!clientId) return thunkApi.dispatch(fetchLienholderDetails(fetchInput));

      if (clientId) fetchInput.clientId = clientId;
      return thunkApi.dispatch(fetchLienholderWithLenderDetails(fetchInput));
    } catch {
      InsighttNotification('error', UPSERT_TRR_LIENHOLDER_ERROR_MSG);
      return thunkApi.rejectWithValue(UPSERT_TRR_LIENHOLDER_ERROR_MSG);
    }
  },
);

export const fetchNewTopAgentsAction = createAsyncThunk<
  TopRecoveryAgentQuery['topRecoveryAgent'],
  FetchNewTopAgentsParams,
  { state: AppState }
>(FETCH_TOP_AGENTS_THUNK_TYPE, async (data, thunkApi) => {
  const store = thunkApi.getState() as AppState;
  const timezone = store.system.session.timezone;

  if (!timezone) {
    return thunkApi.rejectWithValue(new Error('No timezone'));
  }

  const { startDate, endDate, branchId } = data;

  const startDateTime = moment.tz(startDate, timezone).startOf('day').toISOString();
  const endDateTime = moment.tz(endDate, timezone).endOf('day').toISOString();

  const input = {
    startDateTime,
    endDateTime,
    branchId,
  };

  try {
    const response = await fetchNewTopRecoveryAgents(client, input);

    if (!response) throw new Error(FETCH_TOP_AGENTS_THUNK_ERROR);

    return thunkApi.fulfillWithValue(response);
  } catch {
    InsighttNotification('error', FETCH_TOP_AGENTS_THUNK_ERROR);
    return thunkApi.rejectWithValue(FETCH_TOP_AGENTS_THUNK_ERROR);
  }
});

/**
 * @deprecated - use useTopRecoveryAgentViewAll instead.
 */
export const fetchNewTopAgentsViewAllAction = createAsyncThunk(
  FETCH_TOP_AGENTS_VIEW_ALL_THUNK_TYPE,
  async (data: FetchNewTopAgentsParams, thunkApi) => {
    const store = thunkApi.getState() as AppState;
    const timezone = store.system.session.timezone;

    if (!timezone) {
      return thunkApi.rejectWithValue(new Error('No timezone'));
    }

    const { startDate, endDate, branchId } = data;

    const startDateTime = moment.tz(startDate, timezone).startOf('day').toISOString();
    const endDateTime = moment.tz(endDate, timezone).endOf('day').toISOString();

    const input = {
      startDateTime,
      endDateTime,
      branchId,
    };

    try {
      const response = await fetchNewTopRecoveryAgentsViewAll(client, input);

      if (!response) throw new Error(FETCH_TOP_AGENTS_VIEW_ALL_THUNK_ERROR);

      return thunkApi.fulfillWithValue(response);
    } catch {
      InsighttNotification('error', FETCH_TOP_AGENTS_VIEW_ALL_THUNK_ERROR);
      return thunkApi.rejectWithValue(FETCH_TOP_AGENTS_VIEW_ALL_THUNK_ERROR);
    }
  },
);

export const setTopAgentsViewAll = createAction<TopRecoveryAgentViewAllOutput[]>('SET_TOP_AGENTS_VIEW_ALL');
